import { useEffect, useState } from "react";
import { useTarry } from "../../../components/tarry/TarryContext";

export default function Hero() {
  const [mounted, setMounted] = useState(false);
  const { openWithMessage } = useTarry();
  useEffect(() => { setMounted(true); }, []);

  return (
    <section className="relative min-h-screen pt-[70px] bg-white overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #DBEAFE 0%, transparent 70%)", transform: "translate(20%, -20%)" }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, #CCFBF1 0%, transparent 70%)", transform: "translate(-20%, 20%)" }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div className={`transition-all duration-700 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-6 border"
              style={{ background: "linear-gradient(135deg, #EFF6FF, #F0FDFA)", borderColor: "#BFDBFE", color: "#2563EB" }}
            >
              <i className="ri-flashlight-line w-4 h-4 flex items-center justify-center" />
              AI-Powered Payment Infrastructure
            </div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-[68px] font-bold leading-[1.1] tracking-tight text-gray-900 mb-5">
              Smart Payments.<br />
              <span style={{ background: "linear-gradient(135deg, #2563EB, #14B8A6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                Real Results.
              </span>
            </h1>

            {/* Subtext */}
            <p className="text-lg md:text-xl text-gray-500 leading-relaxed max-w-md mb-8">
              The intelligent payment gateway for modern businesses and platforms.
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-4 animate-slide-up" style={{ animationDelay: "0.2s" }}>
              <button
                onClick={() => openWithMessage('I want to talk to sales about Paygent payment solutions.')}
                className="px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 hover:shadow-lg transition-all transform hover:-translate-y-0.5 cursor-pointer"
              >
                Contact Sales
              </button>
              <a
                href="/developers/docs"
                className="px-8 py-4 bg-white text-gray-700 font-bold rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                View Documentation
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap gap-6 sm:gap-8 mt-10">
              {[
                { icon: "ri-shield-check-line", value: "99.99%", label: "Uptime SLA" },
                { icon: "ri-brain-line", value: "AI Fraud Engine", label: "Real-time protection" },
                { icon: "ri-global-line", value: "Global Scale", label: "150+ countries" },
              ].map((item) => (
                <div key={item.value} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg"
                    style={{ background: "linear-gradient(135deg, #EFF6FF, #F0FDFA)" }}>
                    <i className={`${item.icon} text-blue-600 text-sm w-4 h-4 flex items-center justify-center`} />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{item.value}</div>
                    <div className="text-xs text-gray-500">{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: 3D Illustration */}
          <div className={`relative flex items-center justify-center transition-all duration-700 delay-200 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border border-gray-200/60 bg-white/50 backdrop-blur-sm animate-fade-in" style={{ animationDelay: "0.4s" }}>
              <img
                src="/products/hero_dashboard.png"
                alt="Paygent Dashboard"
                className="w-full h-auto"
              />
              {/* Floating elements */}
              <div className="absolute top-8 right-8 animate-bounce-slow">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white border border-blue-100"
                  style={{ boxShadow: "0 8px 24px rgba(37,99,235,0.15)" }}>
                  <i className="ri-lock-2-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center" />
                </div>
              </div>
              <div className="absolute bottom-16 left-4 animate-pulse-slow">
                <div className="px-3 py-1.5 rounded-lg bg-white border border-green-100 flex items-center gap-1.5"
                  style={{ boxShadow: "0 4px 12px rgba(16,185,129,0.15)" }}>
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs font-semibold text-gray-700">Payment verified</span>
                </div>
              </div>
              <div className="absolute top-24 left-0 animate-float">
                <div className="px-3 py-1.5 rounded-lg bg-white border border-blue-100 flex items-center gap-1.5"
                  style={{ boxShadow: "0 4px 12px rgba(37,99,235,0.12)" }}>
                  <i className="ri-arrow-up-line text-green-500 text-xs w-3 h-3 flex items-center justify-center" />
                  <span className="text-xs font-semibold text-green-600">+18.6% revenue</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
        .animate-float { animation: float 4s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 2.5s ease-in-out infinite; }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.15s ease-out; }
      `}</style>
    </section>
  );
}
